/**
 * Màn **Chi phí AI** — sổ cái và cầu dao.
 *
 * Hai thứ này khác nhau và màn hình phải nói rõ: **sổ cái ĐẾM, cầu dao CHẶN.** Một sổ cái ghi đầy
 * đủ mà không ai chặn chỉ là bản tường thuật chi tiết về việc số dư đã bay đi đâu.
 *
 * Luật quan trọng nhất ở đây: **`soDongSoCai === 0` KHÔNG phải "đã tiêu $0"** — nó là *chưa đo
 * được*. Sổ cái rỗng có thể nghĩa là hôm nay chưa gọi model lần nào, hoặc sổ cái đang hỏng và mọi
 * lời gọi đều không ghi được. Hai chuyện đó dẫn tới hai hành động hoàn toàn khác nhau.
 *
 * Và **dòng lỗi vẫn là dòng có thật** (`AP-45`): nếu chỉ đếm lời gọi thành công thì "một ngày mọi
 * lời gọi đều hỏng" và "một ngày không chạy gì" là cùng một bảng rỗng.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type DongSoCai } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  BocBang,
  Chip,
  ChuaDo,
  DangTai,
  DanhSach,
  DauBang,
  DauThe,
  DauTrang,
  Doc,
  Hang,
  Khoi,
  LuoiDoc,
  Nut,
  OBang,
  OTieuDe,
  Rong,
  So,
  ThanBang,
  The,
} from "../thanh-phan/giao-dien.tsx";

function usd(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export function ChiPhi() {
  const [chiLoi, datChiLoi] = useState(false);

  const tq = useQuery({ queryKey: khoa.chiPhiTongQuan, queryFn: api.chiPhiTongQuan });
  const theoTacVu = useQuery({ queryKey: khoa.chiPhiTheoTacVu, queryFn: api.chiPhiTheoTacVu });
  const soCai = useQuery({
    queryKey: [...khoa.chiPhiSoCai, chiLoi],
    queryFn: () => api.chiPhiSoCai(chiLoi, 100),
  });

  if (tq.error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Chi phí AI" />
        <Bao muc="loi" tieuDe="Không tải được">
          {tq.error.message}
        </Bao>
      </div>
    );
  }
  if (tq.isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Chi phí AI" />
        <DangTai />
      </div>
    );
  }

  const t = tq.data;
  // Sổ cái rỗng ⇒ **chưa đo được**, không phải $0. Đây là toàn bộ điểm của biến này.
  const chuaDoDuoc = t.soDongSoCai === 0;
  const tiLeNgay = t.tranNgayUsd > 0 ? Math.min(100, (t.ngayUsd / t.tranNgayUsd) * 100) : 0;
  const tiLeThang = t.tranThangUsd > 0 ? Math.min(100, (t.thangUsd / t.tranThangUsd) * 100) : 0;

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Chi phí AI"
        moTa="Sổ cái ghi một dòng mỗi lời gọi, kể cả khi lỗi. Cầu dao chặn trước lời gọi, không phải sau."
      />

      {chuaDoDuoc && (
        <Bao muc="canh" tieuDe="Sổ cái đang RỖNG — chưa đo được, không phải đã tiêu $0">
          Chưa có dòng nào trong <code>AiUsage</code>. Có thể chưa gọi model lần nào, cũng có thể sổ
          cái đang hỏng và mọi lời gọi đều không ghi được. Hai chuyện đó cần hai cách xử lý khác
          nhau, nên màn này không hiện &ldquo;$0&rdquo; như một sự thật đã đo.
        </Bao>
      )}

      <LuoiDoc cot={4}>
        <Doc
          nhan="Hôm nay (giờ VN)"
          giaTri={chuaDoDuoc ? <ChuaDo /> : usd(t.ngayUsd)}
          ghiChu={`trần ${usd(t.tranNgayUsd)}${t.tranNgayDaDat ? "" : " (mặc định)"}`}
          sacThai={tiLeNgay >= 80 ? "xau" : undefined}
          canAnh={tiLeNgay >= 50 && tiLeNgay < 80}
        />
        <Doc
          nhan="Tháng này (giờ VN)"
          giaTri={chuaDoDuoc ? <ChuaDo /> : usd(t.thangUsd)}
          ghiChu={`trần ${usd(t.tranThangUsd)}${t.tranThangDaDat ? "" : " (mặc định)"}`}
          sacThai={tiLeThang >= 80 ? "xau" : undefined}
          canAnh={tiLeThang >= 50 && tiLeThang < 80}
        />
        <Doc nhan="Dòng sổ cái" giaTri={t.soDongSoCai} />
        <Doc
          nhan="Dòng lỗi"
          giaTri={t.soDongLoi}
          sacThai={t.soDongLoi > 0 ? "xau" : "tot"}
        />
      </LuoiDoc>

      <The>
        <DauThe
          tieuDe="Cầu dao"
          phu="Chạm trần thì dừng SINH bài mới. KHÔNG dừng đăng bài đã duyệt — bài đó đã tốn tiền rồi."
        />
        <div className="space-y-3">
          {[
            { nhan: "Ngày", da: t.ngayUsd, tran: t.tranNgayUsd, tiLe: tiLeNgay, daDat: t.tranNgayDaDat },
            {
              nhan: "Tháng",
              da: t.thangUsd,
              tran: t.tranThangUsd,
              tiLe: tiLeThang,
              daDat: t.tranThangDaDat,
            },
          ].map((r) => (
            <div key={r.nhan} className="min-w-0">
              <div className="flex min-w-0 items-baseline justify-between gap-2 text-2xs">
                <span className="text-muc-600">
                  {r.nhan}
                  {!r.daDat && <span className="ml-1 text-muc-400">(trần mặc định, chưa ai đặt)</span>}
                </span>
                <span className="shrink-0 text-muc-500">
                  {usd(r.da)} / {usd(r.tran)} · còn {usd(Math.max(0, r.tran - r.da))}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muc-200">
                <div
                  className={
                    r.tiLe >= 80 ? "h-full bg-loi-500" : r.tiLe >= 50 ? "h-full bg-canh-500" : "h-full bg-on-500"
                  }
                  style={{ width: `${Math.max(1, r.tiLe)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-2xs text-muc-500">
          Mốc reset tính theo <strong>giờ Việt Nam</strong>, không phải UTC — dùng UTC thì trần đứt
          vào 07:00 sáng và dashboard báo &ldquo;hôm nay $0&rdquo; ngay sau khi vừa tiêu.
        </p>
      </The>

      <Khoi
        nhan="Tiền đi đâu"
        id="cp-theo-tac-vu"
        moSan
        tomTat={`theo tác vụ, tháng này`}
      >
      <The>
        {theoTacVu.isPending ? (
          <DangTai />
        ) : theoTacVu.error ? (
          <Bao muc="loi">{theoTacVu.error.message}</Bao>
        ) : theoTacVu.data.length === 0 ? (
          <Rong tieuDe="Chưa có lời gọi nào tháng này">
            Không phải lỗi — chỉ là chưa chạy gì.
          </Rong>
        ) : (
          // `BocBang` chứ không phải `<table>` trần: mọi bảng trong app đi qua đó để luật "thân
          // trang không bao giờ cuộn ngang" không thể bị quên ở một màn.
          <BocBang rongToiThieu="26rem">
            <DauBang>
              <tr>
                <OTieuDe>Tác vụ</OTieuDe>
                <OTieuDe canh="phai">Lời gọi</OTieuDe>
                <OTieuDe canh="phai">Lỗi</OTieuDe>
                <OTieuDe canh="phai">Tiền</OTieuDe>
              </tr>
            </DauBang>
            <ThanBang>
              {theoTacVu.data.map((r) => (
                <tr key={r.tacVu}>
                  <OBang className="break-words text-muc-800">{r.tacVu}</OBang>
                  <So>{r.soLoiGoi}</So>
                  <So sacThai={r.soLoi > 0 ? "xau" : "mo"}>{r.soLoi}</So>
                  <So sacThai={r.tienUsd > 0 ? "nhan" : "mo"}>{usd(r.tienUsd)}</So>
                </tr>
              ))}
            </ThanBang>
          </BocBang>
        )}
      </The>
      </Khoi>

      <Khoi
        nhan="Sổ cái"
        id="cp-so-cai"
        tomTat={
          t.soDongLoi > 0 ? (
            <span className="font-medium text-loi-600">{t.soDongLoi} dòng lỗi</span>
          ) : (
            <span className="text-on-600">{t.soDongSoCai} dòng, không lỗi</span>
          )
        }
      >
      <The>
        <DauThe tieuDe="Sổ cái" phu="Dòng lỗi cũng là dòng — chúng là thứ duy nhất cho thấy hệ đang hỏng">
          <Nut kieu="vien" onClick={() => datChiLoi((v) => !v)}>
            {chiLoi ? "Xem tất cả" : "Chỉ dòng lỗi"}
          </Nut>
        </DauThe>

        {soCai.isPending ? (
          <DangTai />
        ) : soCai.error ? (
          <Bao muc="loi">{soCai.error.message}</Bao>
        ) : soCai.data.length === 0 ? (
          <Rong tieuDe={chiLoi ? "Không có dòng lỗi nào" : "Sổ cái rỗng"}>
            {chiLoi ? "Đây là trạng thái tốt." : "Chưa đo được — xem cảnh báo phía trên."}
          </Rong>
        ) : (
          <DanhSach>
            {soCai.data.map((d: DongSoCai) => (
              <Hang key={d._id}>
                <div className="min-w-0 flex-1 px-4 py-2.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <Chip muc={d.thanhCong ? "on" : "loi"}>{d.thanhCong ? "ok" : "lỗi"}</Chip>
                    <span className="truncate text-sm font-medium text-muc-800" title={d.tacVu}>
                      {d.tacVu}
                    </span>
                    <Chip muc="thuong">{d.catalogKey}</Chip>
                    {d.siteSlug && <Chip muc="thuong">{d.siteSlug}</Chip>}
                    <span className="so-thang-cot ml-auto shrink-0 text-sm text-muc-800">
                      {d.tienUsd === 0 ? <ChuaDo nhan="không tốn tiền" /> : usd(d.tienUsd)}
                    </span>
                  </div>
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 text-2xs text-muc-500">
                    <span>vào {d.tokenVao}</span>
                    <span className="text-muc-300">·</span>
                    <span>ra {d.tokenRa}</span>
                    {d.tokenThinking > 0 && (
                      <>
                        <span className="text-muc-300">·</span>
                        <span>thinking {d.tokenThinking}</span>
                      </>
                    )}
                    <span className="text-muc-300">·</span>
                    <span>{d.giayChay}s</span>
                    <span className="text-muc-300">·</span>
                    <span>{gioVN(d.luc)}</span>
                  </div>
                  {d.loi !== null && (
                    <p className="mt-1 min-w-0 break-words text-2xs text-loi-600">{d.loi}</p>
                  )}
                </div>
              </Hang>
            ))}
          </DanhSach>
        )}
      </The>
      </Khoi>
    </div>
  );
}
